import { all, call, put, takeEvery } from 'redux-saga/effects';
import { IRequestParams, IResponse } from '@dash/apiInstance';
import { sendRequest } from 'src/helpers/saga';
import notifications from 'src/redux/notifications/actions';
import { logger } from 'src/helpers/logger';
import { tableConfig } from 'src/settings';
import ApiInstance from 'src/helpers/apiInstance';
import {
  LoadChecklistsAction,
  LoadChecklistDetailAction,
  LoadDataAction,
  LoadShiftsAction,
  LoadShiftHistoryPositionsAction,
  LoadTenureTracksAction,
} from './actions.d';
import actions from './actions';
import actionTypes from './actionTypes';
import { IShiftHistoryPositionsResponse, ShiftInfo } from '../types';

const API_ROOT = 'agencies/worker_identities';

const api = new ApiInstance(`${API_ROOT}/:id`);
const shiftsApi = new ApiInstance(`${API_ROOT}/:id/shifts/:tab`);
const tenureApi = new ApiInstance('agencies/:agency_id/worker_identities/:worker_identity_id/tenure_tracks');
const positionsApi = new ApiInstance('agencies/:agency_id/worker_identities/:worker_identity_id/positions');
const checklistsApi = new ApiInstance('agencies/:agency_id/worker_identities/:worker_identity_id/checklists');
const checklistDetailApi = new ApiInstance(
  'agencies/:agency_id/worker_identities/:worker_identity_id/checklists/:checklist_id',
);

function normalizeRecords(records: ShiftInfo[] = []) {
  return records.map((row, index) => {
    const group_id = row.id ?? `row-${index}`;

    if (Array.isArray(row.shifts) && row.shifts.length === 1) {
      return { ...row, group_id };
    }

    if (Array.isArray(row.shifts) && row.shifts.length > 1) {
      return {
        ...row,
        group_id,
        children: row.shifts.map(shift => ({
          group_id,
          ...shift,
        })),
      };
    }

    return { ...row, group_id };
  });
}

const filterOrder = ['legal_entity_id', 'position_id', 'status'];

function orderFilters(filters: any[] = []) {
  return [...filters].sort((a, b) => filterOrder.indexOf(a.name) - filterOrder.indexOf(b.name));
}

const checklistsFilterOrder = ['legal_entity', 'company', 'position'];

function orderChecklistsFilters(filters: any[] = []) {
  return [...filters].sort((a, b) => checklistsFilterOrder.indexOf(a.name) - checklistsFilterOrder.indexOf(b.name));
}

function* loadData({ payload }: LoadDataAction): Generator<any, void, any> {
  try {
    const { id } = payload;
    const result: IResponse<any> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      api.get,
      { pathParams: { id } },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200 && result.data) {
      const { worker_identity } = result.data;

      yield put(actions.loadDataSuccess({ worker_identity }));
    } else {
      yield put(actions.loadDataError());
    }
  } catch (err) {
    logger.error(err);
  }
}

function* loadWorkerShifts({ payload }: LoadShiftsAction<{}, {}>): Generator<any, void, any> {
  try {
    const {
      page = 1,
      pageSize = tableConfig.pageSize,
      column,
      order,
      filter = '',
      agency_ids,
      tab,
      statuses,
      id,
    } = payload;

    const result: IResponse<any> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      shiftsApi.get,
      { pathParams: { id, tab }, search: { page, pageSize, column, order, filter, agency_ids, statuses } },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200 && result.data) {
      const { records, filters, filteredCount, totalCount } = result.data;
      const normalized = normalizeRecords(records);
      yield put(
        actions.loadShiftsSuccess({
          records: normalized,
          filters,
          filteredCount,
          totalCount,
        }),
      );
    } else {
      yield put(actions.loadShiftsError());
    }
  } catch (err) {
    logger.error(err);
  }
}

function* loadTenureTracks({ payload }: LoadTenureTracksAction<{}, {}>): Generator<any, void, any> {
  try {
    const {
      page = 1,
      pageSize = tableConfig.pageSize,
      column,
      order,
      agency_id,
      worker_identity_id,
      position_id,
      status,
      legal_entity_id,
    } = payload;

    const result: IResponse<any> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      tenureApi.get,
      {
        pathParams: { agency_id, worker_identity_id },
        search: { page, pageSize, column, order, position_id, status, legal_entity_id },
      },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200 && result.data) {
      const { records, filters, filteredCount, totalCount } = result.data;

      yield put(
        actions.loadTenureTracksSuccess({
          agency_id,
          records,
          filters: orderFilters(filters),
          filteredCount,
          totalCount,
        }),
      );
    } else {
      yield put(actions.loadTenureTracksError());
    }
  } catch (err) {
    yield put(actions.loadTenureTracksError());
    logger.error(err);
  }
}

function* loadShiftHistoryPositions({ payload }: LoadShiftHistoryPositionsAction): Generator<any, void, any> {
  try {
    const { agency_id, worker_identity_id } = payload;

    const result: IResponse<IShiftHistoryPositionsResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      positionsApi.get,
      { pathParams: { agency_id, worker_identity_id } },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200 && result.data) {
      yield put(
        actions.loadShiftHistoryPositionsSuccess({
          agency_id,
          positions: result.data.positions ?? [],
        }),
      );
    } else {
      yield put(actions.loadShiftHistoryPositionsError());
    }
  } catch (err) {
    yield put(actions.loadShiftHistoryPositionsError());
    logger.error(err);
  }
}

function* loadChecklists({ payload }: LoadChecklistsAction<{}, {}>): Generator<any, void, any> {
  try {
    const {
      page = 1,
      pageSize = tableConfig.pageSize,
      column,
      order,
      agency_id,
      worker_identity_id,
      legal_entity,
      position,
      company,
    } = payload;

    const result: IResponse<any> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      checklistsApi.get,
      {
        pathParams: { agency_id, worker_identity_id },
        search: { page, pageSize, column, order, legal_entity, position, company },
      },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200 && result.data) {
      const { records, filters, filteredCount, totalCount } = result.data;

      yield put(
        actions.loadChecklistsSuccess({
          agency_id,
          records,
          filters: orderChecklistsFilters(filters),
          filteredCount,
          totalCount,
        }),
      );
    } else {
      yield put(actions.loadChecklistsError());
    }
  } catch (err) {
    yield put(actions.loadChecklistsError());
    logger.error(err);
  }
}

function* loadChecklistDetail({ payload }: LoadChecklistDetailAction): Generator<any, void, any> {
  try {
    const { agency_id, worker_identity_id, checklist_id } = payload;

    const result: IResponse<any> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      checklistDetailApi.get,
      { pathParams: { agency_id, worker_identity_id, checklist_id } },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200 && result.data) {
      yield put(actions.loadChecklistDetailSuccess({ checklist: result.data }));
    } else {
      yield put(actions.loadChecklistDetailError());
    }
  } catch (err) {
    yield put(actions.loadChecklistDetailError());
    logger.error(err);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actionTypes.LOAD_DATA, loadData),
    takeEvery(actionTypes.LOAD_SHIFTS, loadWorkerShifts),
    takeEvery(actionTypes.LOAD_TENURE_TRACKS, loadTenureTracks),
    takeEvery(actionTypes.LOAD_SHIFT_HISTORY_POSITIONS, loadShiftHistoryPositions),
    takeEvery(actionTypes.LOAD_CHECKLISTS, loadChecklists),
    takeEvery(actionTypes.LOAD_CHECKLIST_DETAIL, loadChecklistDetail),
  ]);
}
